import {
  Box,
  Tab,
  Tabs,
  AppBar,
  Typography,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  CustomTextField,
  PageContainer,
  AvatarTableCell,
  CustomTableCell,
  GeneralTable,
  CustomTableRow,
  OutlinedButton,
  GeneralModal,
  ActionTableCell,
  EditRequestComparisonTable,
  ConfirmApproveRequestModal,
  ConfirmRejectRequestModal,
} from '../components/index';
import { apiUrl } from '../utilities/apiUrl';
import { formatISOToPHT } from '../utilities/date';
import { getComparisonSections } from '../utilities/editRequest';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useRequestNotification } from '../providers/RequestNotificationContext';

const boxStyle = {
  mx: 0,
  my: 1,
  px: 3,
  py: 2,
};

const boxInfoStyle = {
  bgcolor: '#2C313D',
  width: 1,
  p: 1,
  px: 2,
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const buttonStyle = {
  display: 'flex',
  justifyContent: 'center', // center icon + text
  color: '#FFF',
  transition: 'background-color 0.3s ease-in',
};

const successStyle = {
  ...buttonStyle,
  bgcolor: '#6da799ff',
  '&:hover': {
    bgcolor: '#57af9b !important',
  },
};

const dangerStyle = {
  ...buttonStyle,
  bgcolor: '#d32f2f',
  '&:hover': {
    bgcolor: '#b71c1c !important',
  },
};

const loadingContainer = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
};

export const Requests = () => {
  const [empId] = useOutletContext();
  const [value, setValue] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [sentValue, setSentValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [currEditRequest, setCurrEditRequest] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [requestModal, setRequestModal] = useState(false);
  const [comparisonSection, setComparisonSection] = useState(null);

  // Edit Request
  const [pendingER, setPendingER] = useState([]);
  const [approvedER, setApprovedER] = useState([]);
  const [rejectedER, setRejectedER] = useState([]);

  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check employee permision
  useEffect(() => {
    fetch(apiUrl(`/employee/${empId}`), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (data.data.role !== 'SUPER_ADMIN') {
            window.location.replace('/forbidden');
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [empId]);

  useEffect(() => {
    fetchEditRequestData();
  }, [value, currentPage, isSearching, sentValue]);

  const clearDataOnly = () => {
    setCurrentPage(1);
    setTotalPages(1);
    setTotalResults(0);
  };

  const clearAll = () => {
    setSentValue('');
    setSearchValue('');
    setCurrentPage(1);
    setTotalPages(1);
    setTotalResults(0);
    setIsSearching(false);
  };

  // Tab change handler
  const handleChange = (event, newValue) => {
    clearAll(); // Clear all values when changing tabs
    setValue(newValue);
    localStorage.setItem('requesttabs', newValue);
  };

  // Page change handler
  const handlePageChange = (event, newValue) => {
    setCurrentPage(newValue);
  };

  // Search change handler
  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
  };

  // Search click handler
  const handleSearchClicked = async () => {
    try {
      if (searchValue.trim() === '') {
        // If search is cleared, fallback to normal pagination
        setIsSearching(false);
        setCurrentPage(1);
        return;
      }

      setIsSearching(true);
      setSentValue(searchValue);
      setCurrentPage(1); // reset to first page when new search starts
    } catch (error) {
      console.error('Error starting search:', error);
    }
  };

  // Confirm approve modal handler
  const handleApproveModalOpen = () => {
    setShowApproveConfirmModal(true);
  };

  // Confirm reject modal handler
  const handleRejectModalOpen = () => {
    setShowRejectConfirmModal(true);
  };

  // Fetch all edit requests data on page load
  const fetchEditRequestData = async () => {
    try {
      setIsDataLoading(true);

      // Fetch employees with edit requests
      const statusMap = ['pending', 'approved', 'rejected'];

      // Fetch complete pending edit request
      const urlPending = isSearching
        ? apiUrl(
            `/eis/empeditrequest/complete/search/pending?search=${sentValue}&page=${currentPage}`
          )
        : apiUrl(`/eis/empeditrequest/complete/pending?page=${currentPage}`);
      const responsePending = await fetch(urlPending, {
        method: 'GET',
        credentials: 'include',
      });
      const resultEditRequestsPending = await responsePending.json();
      const completePendingData = resultEditRequestsPending.data;
      const tempPending = completePendingData;

      // Fetch complete approved edit request
      const urlApproved = isSearching
        ? apiUrl(
            `/eis/empeditrequest/complete/search/approved?search=${sentValue}&page=${currentPage}`
          )
        : apiUrl(`/eis/empeditrequest/complete/approved?page=${currentPage}`);
      const responseApproved = await fetch(urlApproved, {
        method: 'GET',
        credentials: 'include',
      });
      const resultEditRequestsApproved = await responseApproved.json();
      const completeApprovedData = resultEditRequestsApproved.data;
      const tempApproved = completeApprovedData;

      // Fetch complete rejected edit request
      const urlRejected = isSearching
        ? apiUrl(
            `/eis/empeditrequest/complete/search/rejected?search=${sentValue}&page=${currentPage}`
          )
        : apiUrl(`/eis/empeditrequest/complete/rejected?page=${currentPage}`);
      const responseRejected = await fetch(urlRejected, {
        method: 'GET',
        credentials: 'include',
      });
      const resultEditRequestsRejected = await responseRejected.json();
      const completeRejectedData = resultEditRequestsRejected.data;
      const tempRejected = completeRejectedData;

      setPendingER(tempPending);
      setApprovedER(tempApproved);
      setRejectedER(tempRejected);

      if (value === 0) {
        setTotalResults(resultEditRequestsPending.pagination.total);
        setTotalPages(resultEditRequestsPending.pagination.totalPages);
      } else if (value === 1) {
        setTotalResults(resultEditRequestsApproved.pagination.total);
        setTotalPages(resultEditRequestsApproved.pagination.totalPages);
      } else {
        setTotalResults(resultEditRequestsRejected.pagination.total);
        setTotalPages(resultEditRequestsRejected.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsDataLoading(false);
    }
  };

  // Generic fetch function for any employee eis endpoints
  async function fetchEmpData(emp, endpoint) {
    const url = apiUrl(`/eis/${endpoint}/${emp._id}`);
    if (!url) return null;

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      if (!data.success) return [];
      return data?.data || [];
    } catch (err) {
      return [];
    }
  }

  const handleInspectClick = async (er) => {
    const url = apiUrl(`/employee/${er.empid}`);
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    const responseData = await response.json();
    const emp = responseData.data;

    // Set selected employee
    setSelectedEmployee(emp);

    // Fetch extra employee data
    const currEmpAddress = await fetchEmpData(emp, 'empaddress');
    const currEmpRelations = await fetchEmpData(emp, 'emprelations');
    const currEmpEducation = await fetchEmpData(emp, 'empeducation');
    const currEmpCivService = await fetchEmpData(emp, 'empcivservice');
    const currEmpWorkExp = await fetchEmpData(emp, 'empworkexp');
    const currEmpCivicWork = await fetchEmpData(emp, 'empcivicwork');
    const currEmpTraining = await fetchEmpData(emp, 'emptraining');
    const currEmpHistory = await fetchEmpData(emp, 'emphistory');

    // Build sections
    setComparisonSection(
      getComparisonSections(
        er,
        emp,
        currEmpAddress,
        currEmpRelations,
        currEmpEducation,
        currEmpCivService,
        currEmpWorkExp,
        currEmpCivicWork,
        currEmpTraining,
        currEmpHistory
      )
    );

    // Store current request
    setCurrEditRequest(er);

    // Open the modal AFTER everything is set
    setRequestModal(true);
  };

  return (
    <PageContainer selectedPage={5} key={refreshTrigger}>
      <AppBar position="static">
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
        >
          <Tab label="Pending" />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>
      </AppBar>

      {/* Search and Reset Query Fields */}
      <Box sx={{ ...boxStyle }}>
        <Box sx={{ ...boxInfoStyle, gap: 2 }}>
          <Box
            sx={{ display: 'flex', height: 'fit', width: '20%', gap: 1, p: 1 }}
          >
            <CustomTextField
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchClicked();
                }
              }}
              required={false}
              label={'Search (Username, ID)'}
              disabled={false}
              value={searchValue}
              type={'text'}
              style={{
                my: 'auto',
                '& .MuiInputBase-root': {
                  height: 40,
                  fontSize: 13,
                },
                '& .MuiInputLabel-root': {
                  fontSize: 13,
                  top: '-6px',
                },
                '& .MuiInputBase-input': {
                  padding: '6px 8px',
                },
              }}
            />

            <IconButton
              onClick={handleSearchClicked}
              sx={{
                my: 'auto',
                p: '8px',
                minWidth: 'auto',
                height: 'auto',
                bgcolor: '#3A4152',
                color: '#84FFFF',
                '&:hover': {
                  bgcolor: '#4A4F61',
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          </Box>
          <OutlinedButton
            onClick={() => {
              clearAll();
              fetchEditRequestData(1);
            }}
            style={{ flex: 1, flex: 'none', textAlign: 'center' }}
            btnTitle={'Reset Query'}
          />
        </Box>
      </Box>

      {/* Table of employees */}
      <Box sx={{ ...boxStyle }}>
        <Typography variant="h6" sx={{ color: '#84d8c5' }} gutterBottom>
          Employees with{' '}
          {value === 0 ? 'Pending' : value === 1 ? 'Approved' : 'Rejected'} Edit
          Requests
        </Typography>

        {isDataLoading ? (
          <Box sx={loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <GeneralTable
            sx={{
              borderRadius: '5px',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
              mb: 3.6,
              display: 'block',
            }}
            length={totalResults}
            entryPerPage={1} //Set to 1 because the backend handles the pagination already
            pageCount={totalPages}
            pageCountExact={true}
            page={currentPage}
            handlePageChange={handlePageChange}
            noRecord={!isDataLoading && totalResults === 0}
            currentPage={currentPage}
          >
            {totalResults > 0 && (
              <TableHead>
                <TableRow>
                  <CustomTableCell align="left" value="Employee Name" />
                  <CustomTableCell align="center" value="Username" />
                  <CustomTableCell align="center" value="Nickname" />
                  <CustomTableCell align="center" value="Employee ID" />
                  <CustomTableCell align="center" value="Creation Date" />
                  <CustomTableCell align="center" value="Action" />
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {(() => {
                // select the correct queryset based on value
                const dataMap = [pendingER, approvedER, rejectedER];
                const currentData = dataMap[value];

                return currentData.map((er, index) => (
                  <CustomTableRow key={index} hover={true}>
                    <AvatarTableCell
                      scope="row"
                      value={`${er.firstname} ${er.middlename} ${er.lastname}`}
                    />
                    <CustomTableCell
                      align="center"
                      scope="row"
                      value={er.username}
                    />
                    <CustomTableCell
                      align="center"
                      scope="row"
                      value={er.nickname}
                    />
                    <CustomTableCell
                      align="center"
                      scope="row"
                      value={er.empid}
                    />
                    <CustomTableCell
                      align="center"
                      scope="row"
                      value={formatISOToPHT(er.timestamp)}
                    />
                    <ActionTableCell>
                      <IconButton
                        id={index}
                        size="small"
                        onClick={() => handleInspectClick(er)}
                      >
                        <SearchIcon />
                      </IconButton>
                    </ActionTableCell>
                  </CustomTableRow>
                ));
              })()}
            </TableBody>

            {/* COMPARISON MODAL */}
            {selectedEmployee && (
              <GeneralModal
                style={{ width: '90%', p: 3, height: '90vh' }}
                open={requestModal}
                onClose={() => setRequestModal(false)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  {/* HEADER */}
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }} variant="h6">
                      {`${value === 0 ? 'Pending' : value === 1 ? 'Approved' : 'Rejected'}
                      Edit Requests - 
                      ${selectedEmployee.firstname} 
                      ${selectedEmployee.middlename} 
                      ${selectedEmployee.lastname}`}
                    </Typography>

                    <IconButton
                      onClick={() => setRequestModal(false)}
                      sx={{ ml: 'auto' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* COMPARISON TABLE */}
                  {comparisonSection && (
                    <EditRequestComparisonTable
                      comparisonSections={comparisonSection}
                    />
                  )}

                  {/* ACTION BUTTONS */}
                  {value === 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'end',
                        mr: 2,
                        mt: 1.5,
                        gap: 1.5,
                      }}
                    >
                      <Button
                        size="medium"
                        sx={dangerStyle}
                        variant="contained"
                        onClick={handleRejectModalOpen}
                      >
                        Reject
                      </Button>

                      <Button
                        size="medium"
                        sx={successStyle}
                        variant="contained"
                        onClick={handleApproveModalOpen}
                      >
                        Approve
                      </Button>
                    </Box>
                  )}
                </Box>
              </GeneralModal>
            )}
          </GeneralTable>
        )}
      </Box>

      {/* CONFIRM APPROVE MODAL */}
      <ConfirmApproveRequestModal
        open={showApproveConfirmModal}
        onClose={() => setShowApproveConfirmModal(false)}
        editRequest={currEditRequest}
        setRequestModal={setRequestModal}
        fetchEditRequestData={fetchEditRequestData}
        onSuccess={() => setRefreshTrigger((c) => c + 1)}
      />

      {/* CONFIRM REJECT MODAL */}
      <ConfirmRejectRequestModal
        open={showRejectConfirmModal}
        onClose={() => setShowRejectConfirmModal(false)}
        editRequest={currEditRequest}
        setRequestModal={setRequestModal}
        fetchEditRequestData={fetchEditRequestData}
        onSuccess={() => setRefreshTrigger((c) => c + 1)}
      />
    </PageContainer>
  );
};
